import { api } from '@/lib/api/client';
import type {
  Payment,
  PaymentFilters,
  PaymentsResponse,
  CreatePaymentDto,
  UpdatePaymentDto,
  AttachBillDto,
  PaymentMethod,
  PaymentMethodsResponse,
} from '../types';

const PAYMENTS_URL = '/payments';
const PAYMENT_METHODS_URL = '/payment-methods';

export const paymentsApi = {
  // Get all payments with filters
  getAll: async (filters?: PaymentFilters): Promise<PaymentsResponse> => {
    const params: Record<string, unknown> = { ...filters };

    if (params['page'] !== undefined && params['limit']) {
      params['offset'] = (params['page'] as number) * (params['limit'] as number);
      delete params['page'];
    }

    // Remove empty values
    Object.keys(params).forEach((key) => {
      if (params[key] == null || params[key] === '') {
        delete params[key];
      }
    });

    return api.get<PaymentsResponse>(PAYMENTS_URL, { params });
  },

  // Get single payment by ID
  getById: async (id: number | string): Promise<Payment> => {
    return api.get<Payment>(`${PAYMENTS_URL}/${id}`);
  },

  // Create new payment
  create: async (data: CreatePaymentDto): Promise<Payment> => {
    return api.post<Payment>(PAYMENTS_URL, data);
  },

  // Update payment
  update: async ({ id, data }: { id: number | string; data: UpdatePaymentDto }): Promise<Payment> => {
    return api.patch<Payment>(`${PAYMENTS_URL}/${id}`, data);
  },

  // Delete payment
  delete: async (id: number | string): Promise<void> => {
    return api.delete(`${PAYMENTS_URL}/${id}`);
  },

  // Allocate an unallocated (advance) payment to an UNPAID bill
  attachBill: async ({
    id,
    data,
  }: {
    id: number | string;
    data: AttachBillDto;
  }): Promise<Payment> => {
    return api.put<Payment>(`${PAYMENTS_URL}/${id}/attach-bill`, data);
  },
};

export const paymentMethodsApi = {
  // Get all payment methods
  getAll: async (): Promise<PaymentMethodsResponse> => {
    return api.get<PaymentMethodsResponse>(PAYMENT_METHODS_URL);
  },

  // Get single payment method by ID
  getById: async (id: number | string): Promise<PaymentMethod> => {
    return api.get<PaymentMethod>(`${PAYMENT_METHODS_URL}/${id}`);
  },
};

// Query keys for React Query
export const paymentsQueryKeys = {
  all: ['payments'] as const,
  lists: () => [...paymentsQueryKeys.all, 'list'] as const,
  list: (filters?: PaymentFilters) => [...paymentsQueryKeys.lists(), filters] as const,
  details: () => [...paymentsQueryKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...paymentsQueryKeys.details(), id] as const,
};

export const paymentMethodsQueryKeys = {
  all: ['paymentMethods'] as const,
  list: () => [...paymentMethodsQueryKeys.all, 'list'] as const,
};
